import React from 'react';
import { EnumMappers, formatAgency } from '~/common';
import masked from '~/common/masked';
import moment from 'moment/moment';
import { ClearSaleScore } from '~/components';
import { AGREEMENT_NAMES, consignee } from '~/common/constants';
import { PortOutStepLogs } from '../components/PortOutStepLogs';
import OutstandingBalanceValue from '../components/OutstandingBalanceValue';
import { formatGuarantee, formatGuaranteeMoney, formatGuaranteePercent } from './guaranteeFormatters';

const { OperationStepType, AccountType, ValidationStatusUnico, ContractStatus, LoanType } = EnumMappers;

const { toPersonDocument, toPhone, toNumber, toMoney, toDate, toPercentage } = masked;

const ALERT_COLORS = {
  RED: '#c0392b',
  ORANGE: '#eb8934',
  YELLOW: '#f1c40f',
  GREEN: '#52b771',
};

type Block = {
  type?: string;
  columns: Array<any>;
  title: string;
};

type DefaultFields = Array<Block>;

// Espaçador invisível: ocupa uma linha (min-height da Column) sem renderar conteúdo.
// Usado para alinhar a linha de garantias na coluna que tem menos itens.
const emptyRowSpacer = {
  label: '',
  actionType: 'copy',
  value: () => '',
  renderValue: () => <span aria-hidden />,
};

const fields: DefaultFields = [
  {
    title: 'Status score',
    columns: [
      [
        {
          label: 'Status lógico',
          actionType: ({ pendencies }) => (pendencies?.length > 1 ? 'open_modal' : 'copy'),
          clickable: ({ pendencies }) => pendencies?.length > 1,
          value: ({ contractStatus, contractStatus2, pendencies }) => {
            if (pendencies?.length > 1) {
              return `${pendencies.length} status pendentes, clique para exibir`;
            }

            return contractStatus2 ? `${contractStatus} - ${contractStatus2}` : contractStatus;
          },
          modalName: 'PENDENCIES',
          modalParams: ({ pendencies }) => ({ pendencies }),
        },
        {
          label: 'Score clear sale biométrico',
          actionType: 'copy',
          tooltip: ({ biometricScore }) => {
            if (biometricScore !== 0 && !biometricScore) return null;
            const value = parseFloat(biometricScore);
            if (value <= 0.19) return 'Altíssimo Risco';
            if (value >= 0.2 && value <= 0.39) return 'Alto Risco';
            if (value >= 0.4 && value <= 0.79) return 'Médio Risco';
            return 'Baixo Risco';
          },
          value: ({ biometricScore }) => biometricScore,
          valueColor: (value: number) => {
            if (value <= 0.19) return ALERT_COLORS.RED;
            if (value >= 0.2 && value <= 0.39) return ALERT_COLORS.ORANGE;
            if (value >= 0.4 && value <= 0.79) return ALERT_COLORS.YELLOW;
            return ALERT_COLORS.GREEN;
          },
        },
      ],
      [
        {
          label: 'Passo da esteira',
          actionType: 'open_modal',
          value: ({ operationStep }) => OperationStepType(operationStep),
          clickable: true,
          modalName: 'STEP_MODAL',
          modalParams: ({ contractIdParam }: any) => ({ contractId: contractIdParam }),
        },
        {
          label: 'Score UNICO',
          actionType: 'copy',
          tooltip: ({ unicoValidationStatus }) => ValidationStatusUnico(unicoValidationStatus),
          value: ({ scoreUnico, unicoValidationStatus }) =>
            ['VALID', 'INCONCLUSIVE', 'INVALID'].includes(unicoValidationStatus) ? scoreUnico : '-',
          valueColor: (value: number) => (value > 10 ? ALERT_COLORS.GREEN : ALERT_COLORS.RED),
        },
      ],
      [
        {
          label: 'Status da consignatária pessoa',
          actionType: 'copy',
          value: 'person.consigneeLastStatus',
        },
        {
          label: 'Score clear sale 2FA',
          actionType: 'open_modal',
          value: ({ person }) => {
            const scores = person?.clearSaleScores ?? [];
            const lastScore = scores.reduce(
              (latest: any, current: any) => (!latest || current.creationDate > latest.creationDate ? current : latest),
              null,
            );
            return <ClearSaleScore score={lastScore?.score} scoreHistory={scores} showPercent={false} />;
          },
        },
      ],
      [
        {
          label: 'Status da consignatária',
          actionType: 'copy',
          value: ({ consigneeLastStatus, consigneeLastStatusRefPort, loanType }) => {
            if (!consigneeLastStatusRefPort || loanType !== 'PORTABILITY') {
              return consigneeLastStatus;
            }
            return consigneeLastStatusRefPort;
          },
        },
        {
          label: 'Validação do documento',
          actionType: 'open_modal',
          value: ({ infos }) => infos?.valid !== undefined && (infos?.valid ? 'SIM' : 'NÃO'),
          clickable: true,
          modalName: 'DATAVALID',
          modalParams: ({ infos, person }) => ({ infos, personId: person?.id }),
        },
      ],
    ],
  },
  {
    type: 'PORT_OUT_LOGS',
    title: 'Ataques de portabilidade',
    columns: [
      [
        {
          label: 'Última atualização',
          actionType: 'copy',
          value: ({ portOutStepLogs }) => {
            if (!moment(portOutStepLogs.logDate).isValid()) return '-';
            return moment(portOutStepLogs.logDate).format('DD/MM/YYYY [às] HH:mm:ss');
          },
        },
        {
          label: 'Comentário',
          actionType: 'copy',
          value: 'portOutStepLogs.logComment',
        },
      ],
      [
        {
          label: 'Passo da esteira',
          actionType: 'copy',
          value: 'portOutStepLogs.operationStepNamePortOut',
        },
      ],
      [
        {
          label: 'Motivo',
          actionType: 'copy',
          value: 'portOutStepLogs.reason',
        },
      ],
      [
        {
          label: 'Histórico de ataques',
          actionType: 'open_modal',
          value: ({ contractId }) => <PortOutStepLogs contractId={contractId} />,
        },
      ],
    ],
  },
  {
    title: 'Contas de pagamento',
    columns: [
      [
        {
          label: 'Informações da conta',
          actionType: 'copy',
          value: 'bankName',
        },
        {
          label: 'Chave Pix',
          actionType: 'copy',
          value: 'pixkey',
        },
        {
          label: 'Tipo de conta do benefício',
          actionType: 'copy',
          value: 'benefitAccountType',
        },
        {
          label: 'Matricula',
          actionType: 'copy',
          value: 'person.enrollment',
        },
      ],
      [
        {
          label: 'Tipo de conta',
          actionType: 'copy',
          value: ({ accountType }) => (accountType ? AccountType(accountType) : 'Não disponível'),
        },
        {
          label: 'Código do local de trabalho',
          actionType: 'copy',
          value: 'person.workCode',
        },
        {
          label: 'Status da reserva',
          actionType: 'copy',
          value: 'reservationStatus',
        },
      ],
      [
        {
          label: 'Agência',
          actionType: 'copy',
          value: ({ agency }) => formatAgency(agency),
        },
        {
          label: 'Convênio',
          actionType: 'copy',
          value: (values: any) => {
            const { agreementId, agreementName } = values.person;
            return AGREEMENT_NAMES?.[agreementId] || agreementName;
          },
        },
        {
          label: 'Produto',
          actionType: 'product_tags',
          value: (values: any) => {
            const usedFutureMargin = values?.usedFutureMargin;
            const loanType = values?.loanType;
            const suggestedOfferType = values?.suggestedOfferType;
            return { usedFutureMargin, loanType, suggestedOfferType };
          },
        },
      ],
      [
        {
          label: 'Conta',
          actionType: 'copy',
          value: ({ checkingAccount, checkingAccountDigit }) =>
            checkingAccount && checkingAccountDigit ? `${checkingAccount}-${checkingAccountDigit}` : 'Não Disponível',
        },
      ],
    ],
  },
  {
    title: 'Informações pessoais',
    columns: [
      [
        {
          label: 'CPF',
          actionType: 'copy',
          value: ({ person: { document } }) => toPersonDocument(document),
        },
        {
          label: 'RG',
          actionType: 'copy',
          value: 'person.registrationNumber',
        },
      ],
      [
        {
          label: 'Nome da mãe',
          actionType: 'copy',
          value: 'person.motherName',
        },
        {
          label: 'Data de nascimento',
          actionType: ({ canEditBornDate }) => (canEditBornDate ? 'open_modal' : 'copy'),
          value: ({ person }) => toDate(person?.birthDate),
          clickable: true,
          modalName: 'UPDATE_BIRTHDATE',
          modalParams: ({ person, contractIdParam }: any) => ({
            birthDate: person?.birthDate,
            personId: person?.id,
            contractId: contractIdParam,
          }),
        },
      ],
      [
        {
          label: 'Telefone',
          actionType: 'copy',
          value: ({ person: { phone } }) => toPhone(phone),
          valueToCopy: (value) => toNumber(value) || '',
        },
        {
          label: 'E-mail',
          actionType: 'copy',
          value: 'person.email',
        },
      ],
      [],
    ],
  },
  {
    title: 'Informações do contrato',
    columns: [
      [
        {
          label: 'Status do contrato',
          actionType: 'copy',
          versionKey: 'status',
          value: ({ turingContractDetailsResponseDTO, loanStatus }) =>
            turingContractDetailsResponseDTO?.status || ContractStatus(loanStatus),
        },
        {
          label: 'Situação do contrato',
          actionType: 'copy',
          versionKey: 'situationStatus',
          value: ({ turingContractDetailsResponseDTO, contractStatus2 }) =>
            turingContractDetailsResponseDTO?.situationStatus || contractStatus2,
        },
        {
          label: 'Status da reserva',
          actionType: 'copy',
          value: 'reservationStatus',
        },
        {
          label: 'Status da anuência',
          actionType: 'copy',
          value: ({ loanConsentHistory }) => loanConsentHistory?.status || '-',
        },
        {
          label: 'Garantia',
          actionType: 'copy',
          value: ({ guarantees }) => formatGuarantee(guarantees?.temGarantias),
        },
      ],
      [
        {
          label: 'Data do pagamento ref port',
          actionType: 'copy',
          value: ({ paymentDateRefPort }) => (paymentDateRefPort ? toDate(paymentDateRefPort) : 'Não consta'),
        },
        {
          label: 'Status de validação',
          actionType: 'copy',
          value: ({ unicoValidationStatus }) => ValidationStatusUnico(unicoValidationStatus),
        },
        {
          label: 'Atendente',
          actionType: 'copy',
          value: ({ contractOwner }) => contractOwner || 'Não atribuído',
        },
        {
          label: 'Anuência válida até',
          actionType: 'copy',
          value: ({ loanConsentHistory }) =>
            loanConsentHistory?.validUntil ? toDate(loanConsentHistory.validUntil, 'DD/MM/YYYY HH:mm:ss') : '-',
        },
        {
          label: 'Garantia - Saldo FGTS',
          actionType: 'copy',
          value: ({ guarantees }) => formatGuaranteeMoney(guarantees?.valorSaldoDisponivelGarantiaFgts),
        },
      ],
      [
        {
          label: 'N do contrato (refin da port)',
          actionType: ({ financialInstitution, financialInstitutionKeyPort }) =>
            financialInstitution?.bankNumber === '326' && !!financialInstitutionKeyPort ? 'copy' : 'open_modal',
          clickable: ({ financialInstitution, financialInstitutionKeyPort }) =>
            financialInstitution?.bankNumber !== '326' && !!financialInstitutionKeyPort,
          modalName: 'UPDATE_CONTRACT_NUMBER',
          modalParams: ({ financialInstitutionKeyPort, contractIdParam }: any) => ({
            initialValue: financialInstitutionKeyPort,
            type: 'REFIN_PORT',
            contractId: contractIdParam,
          }),
          value: 'financialInstitutionKeyPort',
        },
        {
          label: 'N do contrato (CCB)',
          actionType: ({ financialInstitution, financialInstitutionKey }) =>
            financialInstitution?.bankNumber === '326' && !!financialInstitutionKey ? 'copy' : 'open_modal',
          clickable: ({ financialInstitution, financialInstitutionKey }) =>
            financialInstitution?.bankNumber !== '326' && !!financialInstitutionKey,
          modalName: 'UPDATE_CONTRACT_NUMBER',
          modalParams: ({ financialInstitutionKey, contractIdParam }: any) => ({
            initialValue: financialInstitutionKey,
            contractId: contractIdParam,
          }),
          versionKey: 'ccb',
          value: 'financialInstitutionKey',
        },
        {
          label: 'Chave do contrato',
          actionType: 'copy',
          value: 'reservationKey',
        },
        {
          label: 'Último retorno dataprev - anuência',
          actionType: 'copy',
          value: ({ loanConsentHistory }) =>
            loanConsentHistory?.createdAt ? toDate(loanConsentHistory.createdAt, 'DD/MM/YYYY HH:mm:ss') : '-',
        },
        {
          label: 'Garantia - Multa Rescisória',
          actionType: 'copy',
          value: ({ guarantees }) => formatGuaranteeMoney(guarantees?.valorMultaRescisoriaGarantiaFgts),
        },
      ],
      [
        {
          label: 'Consignatária',
          actionType: 'copy',
          value: ({ financialInstitution }) => consignee[financialInstitution?.bankNumber] || '-',
        },
        {
          label: 'Fundo',
          actionType: 'copy',
          versionKey: 'fundingName',
          value: ({ turingContractDetailsResponseDTO }) => turingContractDetailsResponseDTO?.fundingName,
        },
        {
          label: 'Contratação em lote',
          actionType: 'group_contracts',
          value: 'groupContracts',
        },
        emptyRowSpacer,
        {
          label: 'Garantia - % verba rescisória',
          actionType: 'copy',
          value: ({ guarantees }) => formatGuaranteePercent(guarantees?.percVerbaRescisoriaGarantia),
        },
      ],
    ],
  },
  {
    title: 'Valores do contrato',
    columns: [
      [
        {
          label: 'Valor líquido',
          actionType: 'copy',
          versionKey: 'netValue',
          value: ({ loanStatus, netValue, netValueApproved }) =>
            toMoney(loanStatus !== 'CCBSIGNED' && loanStatus !== 'PAID' ? netValue : netValue || netValueApproved),
        },
        {
          label: 'Data/hora inicial',
          actionType: 'copy',
          versionKey: 'contractDate',
          value: ({ contractInitialDate }) => toDate(contractInitialDate),
        },
      ],

      [
        {
          label: 'Valor depósito',
          actionType: 'copy',
          value: ({ loanStatus, valueForDeposit, valueForDepositApproved }) =>
            toMoney(
              loanStatus !== 'CCBSIGNED' && loanStatus !== 'PAID'
                ? valueForDeposit
                : valueForDeposit || valueForDepositApproved,
            ),
        },
        {
          label: 'Data assinatura contrato',
          actionType: 'copy',
          versionKey: 'contractDate',
          value: ({ contractDate, showContractDate }) => (showContractDate ? toDate(contractDate) : 'Indisponível'),
        },
      ],
      [
        {
          label: 'Parcelas',
          actionType: ({ loanType }) => (loanType === LoanType.LOAN_TYPES.FGTS_NEW ? 'open_modal' : 'copy'),
          clickable: ({ loanType }) => loanType === LoanType.LOAN_TYPES.FGTS_NEW,
          modalName: 'FGTS_INSTALLMENTS',
          modalParams: ({ installments }) => ({ installments }),
          versionKey: 'installments',
          value: ({ loanStatus, numberOfInstallments, installmentValue, installmentValueApproved, loanType }) => {
            if (loanType === LoanType.LOAN_TYPES.FGTS_NEW) {
              return `${numberOfInstallments} parcelas anuais`;
            }
            if (loanStatus !== 'CCBSIGNED' && loanStatus !== 'PAID') {
              return `${numberOfInstallments} x ${toMoney(installmentValue)}`;
            }
            return `${numberOfInstallments} x ${toMoney(installmentValue || installmentValueApproved)}`;
          },
        },
        {
          label: 'Data de pagamento',
          actionType: 'copy',
          versionKey: 'paymentDate',
          value: ({ paymentDate }) => toDate(paymentDate),
        },
      ],

      [
        {
          label: 'Valor total',
          actionType: 'copy',
          versionKey: 'total',
          value: ({ turingContractDetailsResponseDTO }) => toMoney(turingContractDetailsResponseDTO?.total),
        },
        {
          label: 'Valor bruto',
          actionType: 'copy',
          versionKey: 'grossValue',
          value: ({ grossValue }) => toMoney(grossValue),
        },
      ],
    ],
  },
  {
    title: 'Taxas',
    columns: [
      [
        {
          label: 'Taxa mensal',
          actionType: 'copy',
          versionKey: 'monthlyRate',
          value: ({ nominalRatePerMonth }) => toPercentage(nominalRatePerMonth),
        },
        {
          label: 'CET mensal',
          actionType: 'copy',
          versionKey: 'monthlyCet',
          value: ({ totalEffectiveCostPerMonth }) => toPercentage(totalEffectiveCostPerMonth),
        },
      ],
      [
        {
          label: 'Taxa anual',
          actionType: 'copy',
          versionKey: 'annualRate',
          value: ({ nominalRatePerYear }) => toPercentage(nominalRatePerYear),
        },
        {
          label: 'CET anual',
          actionType: 'copy',
          versionKey: 'annualCet',
          value: ({ totalEffectiveCostPerYear }) => toPercentage(totalEffectiveCostPerYear),
        },
      ],
      [
        {
          label: 'IOF total',
          actionType: 'copy',
          versionKey: 'totalIof',
          value: ({ iof }) => toMoney(iof),
        },
        {
          label: 'IOF adicional',
          actionType: 'copy',
          versionKey: 'additionalIof',
          value: ({ turingContractDetailsResponseDTO }) => toMoney(turingContractDetailsResponseDTO?.additionalIof),
        },
      ],
    ],
  },
  {
    type: 'OUTSTANDING_BALANCE',
    title: 'Valor para quitação',
    columns: [
      [
        {
          label: 'Data',
          actionType: 'copy',
          value: ({ turingContractDetailsResponseDTO }) =>
            toDate(turingContractDetailsResponseDTO?.outstandingBalance?.date),
        },
      ],
      [
        {
          label: 'Valor',
          actionType: 'copy',
          value: ({ turingContractDetailsResponseDTO }) =>
            toMoney(turingContractDetailsResponseDTO?.outstandingBalance?.value),
          renderValue: ({ turingContractDetailsResponseDTO, person, user, contractIdParam }) => (
            <OutstandingBalanceValue
              value={toMoney(turingContractDetailsResponseDTO?.outstandingBalance?.value)}
              rawValue={turingContractDetailsResponseDTO?.outstandingBalance?.value}
              userId={user?.id}
              personId={person?.id}
              agreementName={person?.agreementName}
              contractId={contractIdParam}
            />
          ),
        },
      ],
    ],
  },
  {
    type: 'EMPLOYER_INFO',
    title: 'Dados do empregador',
    columns: [
      [
        {
          label: 'CNPJ',
          actionType: 'copy',
          versionKey: 'employerCnpj',
          value: ({ turingContractDetailsResponseDTO }) =>
            toPersonDocument(turingContractDetailsResponseDTO?.employer?.cnpj),
        },
      ],
      [
        {
          label: 'CNAE',
          actionType: 'copy',
          versionKey: 'employerCnae',
          value: ({ turingContractDetailsResponseDTO }) => turingContractDetailsResponseDTO?.employer?.cnae,
        },
      ],
    ],
  },
];

export default fields;
